import { AfterViewInit, Directive, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { DxDataGridComponent } from 'devextreme-angular';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { icon as renderFontAwesomeIcon, IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { CustomReportEditor } from '../components/custom-report-editor/models/custom-report-editor.model';

type TableStyleRule = CustomReportEditor.ReportWidgetConditionalRule;
type TableStyleTargetType = 'row' | 'column' | 'header';
type TableStyleDecoration = 'background' | 'text' | 'accentBar' | 'accentDot' | 'icon' | 'badge';

@Directive({
    selector: '[appCustomReportTableStyleRules]',
    standalone: false
})
export class CustomReportTableStyleRulesDirective implements AfterViewInit, OnChanges, OnDestroy {
    @Input('appCustomReportTableStyleRules') rules: TableStyleRule[] | null | undefined;

    private attached = false;
    private readonly rowPreparedHandler = (event: any) => this.onRowPrepared(event);
    private readonly cellPreparedHandler = (event: any) => this.onCellPrepared(event);

    constructor(
        private readonly dataGrid: DxDataGridComponent,
        private readonly iconLibrary: FaIconLibrary
    ) {
    }

    ngAfterViewInit(): void {
        this.attach();
        this.repaint();
    }

    ngOnChanges(_: SimpleChanges): void {
        this.repaint();
    }

    ngOnDestroy(): void {
        const instance = this.dataGrid?.instance as any;
        if (!instance || !this.attached) {
            return;
        }

        instance.off('rowPrepared', this.rowPreparedHandler);
        instance.off('cellPrepared', this.cellPreparedHandler);
        this.attached = false;
    }

    private attach(): void {
        const instance = this.dataGrid?.instance as any;
        if (!instance || this.attached) {
            return;
        }

        instance.on('rowPrepared', this.rowPreparedHandler);
        instance.on('cellPrepared', this.cellPreparedHandler);
        this.attached = true;
    }

    private repaint(): void {
        const instance = this.dataGrid?.instance as any;
        if (!instance) {
            return;
        }

        setTimeout(() => instance.repaint?.());
    }

    private onRowPrepared(event: any): void {
        if (event?.rowType !== 'data') {
            return;
        }

        const rowElement = this.toElement(event.rowElement);
        if (!rowElement) {
            return;
        }

        this.resetTableRuleStyles(rowElement);
        const rowRules = this.getPreparedRules()
            .filter((rule) => this.getTargetType(rule) === 'row')
            .filter((rule) => this.ruleMatches(rule, event.data, event.rowIndex));

        for (const rule of rowRules) {
            if (this.isInlineDecoration(rule)) {
                continue;
            }

            this.applyRuleToElement(rule, rowElement, event.data, event.rowIndex);
        }
    }

    private onCellPrepared(event: any): void {
        const cellElement = this.toElement(event?.cellElement);
        if (!cellElement) {
            return;
        }

        this.resetTableRuleStyles(cellElement);

        if (event?.rowType === 'header') {
            const headerRules = this.getPreparedRules()
                .filter((rule) => this.getTargetType(rule) === 'header')
                .filter((rule) => this.matchesColumn(rule.targetKey, event.column));

            for (const rule of headerRules) {
                this.applyRuleToElement(rule, cellElement, null, event.columnIndex);
            }
            return;
        }

        if (event?.rowType !== 'data') {
            return;
        }

        const matchingRules = this.getPreparedRules()
            .filter((rule) => {
                const targetType = this.getTargetType(rule);
                return targetType === 'row'
                    || (targetType === 'column' && this.matchesColumn(rule.targetKey, event.column));
            })
            .filter((rule) => this.ruleMatches(rule, event.data, event.rowIndex));

        for (const rule of matchingRules) {
            if (this.getTargetType(rule) === 'row' && this.getDecoration(rule) === 'accentBar' && event.columnIndex !== 0) {
                continue;
            }

            if (this.getTargetType(rule) === 'row' && this.isInlineDecoration(rule) && !this.isRowDecorationAnchorCell(rule, event)) {
                continue;
            }

            this.applyRuleToElement(rule, cellElement, event.data, event.rowIndex);
        }
    }

    private getPreparedRules(): TableStyleRule[] {
        return Array.isArray(this.rules)
            ? this.rules
                .filter((rule) => rule && String(rule.appliesTo || '').trim() === 'table')
                .sort((left, right) => Number(left.priority || 0) - Number(right.priority || 0))
            : [];
    }

    private ruleMatches(rule: TableStyleRule, row: any, rowIndex: number): boolean {
        const operator = String(rule.operator || 'eq').trim().toLowerCase();
        if (operator === 'always') {
            return true;
        }

        const value = this.readFieldValue(row, rule.source);
        const expected = rule.value;
        const expectedTo = rule.valueTo;

        switch (operator) {
            case 'empty':
                return this.isEmptyValue(value);
            case 'notempty':
                return !this.isEmptyValue(value);
            case 'eq':
                return this.compareText(value, expected) === 0;
            case 'noteq':
                return this.compareText(value, expected) !== 0;
            case 'contains':
                return String(value ?? '').toLocaleLowerCase().includes(String(expected ?? '').toLocaleLowerCase());
            case 'gt':
            case 'gte':
            case 'lt':
            case 'lte':
            case 'between':
                return this.compareNumber(value, expected, expectedTo, operator);
            default:
                return false;
        }
    }

    private compareText(value: any, expected: any): number {
        const left = String(value ?? '').trim().toLocaleLowerCase();
        const right = String(expected ?? '').trim().toLocaleLowerCase();
        return left === right ? 0 : -1;
    }

    private compareNumber(value: any, expected: any, expectedTo: any, operator: string): boolean {
        const actual = Number(value);
        const lower = Number(expected);
        const upper = Number(expectedTo);
        if (!Number.isFinite(actual) || !Number.isFinite(lower)) {
            return false;
        }

        switch (operator) {
            case 'gt':
                return actual > lower;
            case 'gte':
                return actual >= lower;
            case 'lt':
                return actual < lower;
            case 'lte':
                return actual <= lower;
            case 'between':
                return Number.isFinite(upper) && actual >= lower && actual <= upper;
            default:
                return false;
        }
    }

    private applyRuleToElement(rule: TableStyleRule, element: HTMLElement, row: any, rowIndex: number): void {
        const decoration = this.getDecoration(rule);
        const color = this.resolveRuleColor(rule, row, rowIndex, decoration);
        if (!color) {
            return;
        }

        if (this.isInlineDecoration(rule)) {
            this.applyInlineDecoration(rule, element, row, decoration, color);
            return;
        }

        if (decoration === 'text') {
            element.style.color = color;
            return;
        }

        if (decoration === 'accentBar') {
            element.style.borderLeft = `3px solid ${color}`;
            return;
        }

        element.style.backgroundColor = color;
    }

    private resolveRuleColor(rule: TableStyleRule, row: any, rowIndex: number, decoration: TableStyleDecoration): string {
        const colorMode = String(rule.colorMode || '').trim();
        if (colorMode === 'palette') {
            const palette = Array.isArray(rule.colorPalette)
                ? rule.colorPalette.map((color) => this.safeColor(color)).filter((color) => !!color)
                : [];
            if (!palette.length) {
                return '';
            }

            if (String(rule.colorPaletteMode || '').trim() === 'sourceValue') {
                const value = this.readFieldValue(row, rule.colorPaletteSource || rule.source);
                return palette[this.hashToIndex(String(value ?? ''), palette.length)];
            }

            return palette[Math.abs(Number(rowIndex || 0)) % palette.length];
        }

        const colorSource = decoration === 'text' || decoration === 'icon' ? rule.textColorSource : rule.backgroundColorSource;
        if (colorMode === 'source' || colorSource) {
            const sourceColor = this.safeColor(this.readFieldValue(row, colorSource));
            if (sourceColor) {
                return sourceColor;
            }
        }

        const fixedColor = decoration === 'text' || decoration === 'icon' ? rule.textColor : rule.backgroundColor;
        return this.safeColor(fixedColor);
    }

    private getTargetType(rule: TableStyleRule): TableStyleTargetType {
        const targetType = String(rule.targetType || '').trim();
        if (targetType === 'header') {
            return 'header';
        }

        return targetType === 'column' ? 'column' : 'row';
    }

    private getDecoration(rule: TableStyleRule): TableStyleDecoration {
        const decoration = String(rule.decoration || '').trim();
        if (decoration === 'text') {
            return 'text';
        }

        if (decoration === 'accentBar' || decoration === 'accentDot' || decoration === 'icon' || decoration === 'badge') {
            return decoration;
        }

        return 'background';
    }

    private isInlineDecoration(rule: TableStyleRule): boolean {
        const decoration = this.getDecoration(rule);
        return decoration === 'accentDot' || decoration === 'icon' || decoration === 'badge';
    }

    private applyInlineDecoration(
        rule: TableStyleRule,
        element: HTMLElement,
        row: any,
        decoration: TableStyleDecoration,
        color: string
    ): void {
        const decorationElement = this.createInlineDecorationElement(rule, row, decoration, color);
        if (!decorationElement) {
            return;
        }

        decorationElement.dataset['crTableRuleDecoration'] = 'true';
        decorationElement.style.verticalAlign = 'middle';

        if (this.getPosition(rule) === 'right') {
            decorationElement.style.marginLeft = '0.375rem';
            element.appendChild(decorationElement);
            return;
        }

        decorationElement.style.marginRight = '0.375rem';
        element.insertBefore(decorationElement, element.firstChild);
    }

    private createInlineDecorationElement(
        rule: TableStyleRule,
        row: any,
        decoration: TableStyleDecoration,
        color: string
    ): HTMLElement | null {
        if (decoration === 'accentDot') {
            const dot = document.createElement('span');
            dot.style.display = 'inline-flex';
            dot.style.width = '0.5rem';
            dot.style.height = '0.5rem';
            dot.style.borderRadius = '999px';
            dot.style.backgroundColor = color;
            dot.style.boxShadow = `0 0 0 1px ${color}`;
            return dot;
        }

        if (decoration === 'badge') {
            const label = this.resolveBadgeLabel(rule, row);
            if (!label) {
                return null;
            }

            const badge = document.createElement('span');
            badge.textContent = label;
            badge.style.display = 'inline-flex';
            badge.style.alignItems = 'center';
            badge.style.maxWidth = '100%';
            badge.style.height = '1.25rem';
            badge.style.padding = '0 0.45rem';
            badge.style.borderRadius = '999px';
            badge.style.border = `1px solid color-mix(in srgb, ${color} 72%, #ffffff 28%)`;
            badge.style.backgroundColor = color;
            badge.style.color = this.safeColor(rule.textColor) || '#0f172a';
            badge.style.fontSize = '10px';
            badge.style.fontWeight = '700';
            badge.style.lineHeight = '1';
            badge.style.whiteSpace = 'nowrap';
            badge.style.textTransform = 'uppercase';
            return badge;
        }

        if (decoration === 'icon') {
            return this.createIconElement(rule.icon, color);
        }

        return null;
    }

    private createIconElement(iconName: unknown, color: string): HTMLElement {
        const wrapper = document.createElement('span');
        wrapper.style.display = 'inline-flex';
        wrapper.style.alignItems = 'center';
        wrapper.style.justifyContent = 'center';
        wrapper.style.width = '1rem';
        wrapper.style.height = '1rem';
        wrapper.style.color = color;

        const iconNameText = this.normalizeIconName(iconName) || 'circle-info';
        const definition = (['fal', 'far', 'fas'] as IconPrefix[])
            .map((prefix) => this.iconLibrary.getIconDefinition(prefix, iconNameText as IconName))
            .find((item) => !!item);

        if (!definition) {
            wrapper.textContent = '•';
            wrapper.style.fontSize = '1rem';
            wrapper.style.lineHeight = '1';
            return wrapper;
        }

        const rendered = renderFontAwesomeIcon(definition);
        const template = document.createElement('template');
        template.innerHTML = rendered.html.join('');
        const svg = template.content.firstElementChild as SVGElement | null;
        if (!svg) {
            return wrapper;
        }

        svg.style.width = '0.875rem';
        svg.style.height = '0.875rem';
        svg.style.color = color;
        svg.setAttribute('aria-hidden', 'true');
        wrapper.appendChild(svg);
        return wrapper;
    }

    private resolveBadgeLabel(rule: TableStyleRule, row: any): string {
        if (String(rule.badgeLabelMode || '').trim() === 'source') {
            return String(this.readFieldValue(row, rule.badgeLabelSource || rule.source) ?? '').trim();
        }

        return String(rule.badgeLabel || '').trim();
    }

    private isRowDecorationAnchorCell(rule: TableStyleRule, event: any): boolean {
        const anchorColumn = this.getPosition(rule) === 'right'
            ? this.getLastVisibleDataColumn()
            : this.getFirstVisibleDataColumn();

        if (anchorColumn) {
            return this.matchesColumnReference(event.column, anchorColumn);
        }

        const visibleColumns = this.getVisibleColumns();
        if (!visibleColumns.length) {
            return this.getPosition(rule) === 'right' ? event.columnIndex > 0 : event.columnIndex === 0;
        }

        return this.getPosition(rule) === 'right'
            ? event.columnIndex === visibleColumns.length - 1
            : event.columnIndex === 0;
    }

    private getFirstVisibleDataColumn(): any {
        return this.getVisibleDataColumns()[0] || null;
    }

    private getLastVisibleDataColumn(): any {
        const columns = this.getVisibleDataColumns();
        return columns[columns.length - 1] || null;
    }

    private getVisibleDataColumns(): any[] {
        return this.getVisibleColumns().filter((column) => !column?.command && (column?.dataField || column?.name || column?.caption));
    }

    private getVisibleColumns(): any[] {
        const instance = this.dataGrid?.instance as any;
        const columns = instance?.getVisibleColumns?.();
        return Array.isArray(columns) ? columns : [];
    }

    private matchesColumnReference(left: any, right: any): boolean {
        const leftValues = [left?.dataField, left?.name, left?.caption].map((value) => this.normalizeLookup(value)).filter((value) => !!value);
        const rightValues = [right?.dataField, right?.name, right?.caption].map((value) => this.normalizeLookup(value)).filter((value) => !!value);
        return leftValues.some((leftValue) => rightValues.includes(leftValue));
    }

    private getPosition(rule: TableStyleRule): 'left' | 'right' {
        return String(rule.position || '').trim() === 'right' ? 'right' : 'left';
    }

    private normalizeIconName(icon: unknown): string {
        if (icon === null || icon === undefined) {
            return '';
        }

        if (Array.isArray(icon)) {
            return this.normalizeIconName(icon.length > 1 ? icon[1] : icon[0]);
        }

        if (typeof icon === 'object') {
            const iconLike = icon as { iconName?: unknown; name?: unknown; icon?: unknown };
            return this.normalizeIconName(iconLike.iconName ?? iconLike.name ?? iconLike.icon);
        }

        const raw = String(icon).trim();
        if (!raw) {
            return '';
        }

        const jsonCandidate = raw.replace(/'/g, '"');
        if (
            (jsonCandidate.startsWith('[') && jsonCandidate.endsWith(']'))
            || (jsonCandidate.startsWith('{') && jsonCandidate.endsWith('}'))
        ) {
            try {
                return this.normalizeIconName(JSON.parse(jsonCandidate));
            } catch {
                // Fall through to plain icon-name handling.
            }
        }

        if (!raw.startsWith('[') && !raw.startsWith('{') && raw.includes(':')) {
            return this.normalizeIconName(raw.split(':').pop());
        }

        return raw.replace(/[^a-z0-9-]/gi, '');
    }

    private matchesColumn(targetKey: string | null | undefined, column: any): boolean {
        const target = this.normalizeLookup(targetKey);
        if (!target) {
            return false;
        }

        return [
            column?.dataField,
            column?.name,
            column?.caption
        ].some((value) => this.normalizeLookup(value) === target);
    }

    private readFieldValue(row: any, field: string | null | undefined): any {
        if (!row || typeof row !== 'object') {
            return undefined;
        }

        const key = String(field || '').trim();
        if (!key) {
            return undefined;
        }

        if (Object.prototype.hasOwnProperty.call(row, key)) {
            return row[key];
        }

        const lookup = this.normalizeLookup(key);
        const actualKey = Object.keys(row).find((rowKey) => this.normalizeLookup(rowKey) === lookup);
        return actualKey ? row[actualKey] : undefined;
    }

    private isEmptyValue(value: any): boolean {
        return value === null || value === undefined || String(value).trim().length === 0;
    }

    private normalizeLookup(value: any): string {
        return String(value ?? '').trim().toLocaleLowerCase().replace(/[\s_\-]+/g, '');
    }

    private hashToIndex(value: string, length: number): number {
        const hash = Array.from(value || 'row').reduce((accumulator, character) => accumulator + character.charCodeAt(0), 0);
        return Math.abs(hash) % Math.max(length, 1);
    }

    private safeColor(value: any): string {
        const color = String(value ?? '').trim();
        if (!color) {
            return '';
        }

        if (typeof CSS !== 'undefined' && CSS.supports?.('color', color)) {
            return color;
        }

        return /^(transparent|#[0-9a-f]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\))$/i.test(color) ? color : '';
    }

    private resetTableRuleStyles(element: HTMLElement): void {
        element.style.removeProperty('background-color');
        element.style.removeProperty('color');
        element.style.removeProperty('border-left');
        Array.from(element.children)
            .filter((node) => node instanceof HTMLElement && node.dataset['crTableRuleDecoration'] === 'true')
            .forEach((node) => node.remove());
    }

    private toElement(value: any): HTMLElement | null {
        if (!value) {
            return null;
        }

        if (value instanceof HTMLElement) {
            return value;
        }

        if (value.get?.(0) instanceof HTMLElement) {
            return value.get(0);
        }

        if (value[0] instanceof HTMLElement) {
            return value[0];
        }

        return null;
    }
}
